import React, { useState } from "react";
import {
	Switch,
	Select,
	MenuItem,
	Typography,
	TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

// Editor visual do horário de atendimento humano por conexão. Substitui o
// textarea de JSON cru que existia antes (item 80 do HISTORICO_AJUSTES.md) —
// continua produzindo/consumindo a MESMA string JSON que o backend já lê
// (Whatsapps.businessHours), só que ninguém mais precisa escrever esse JSON
// na mão. String vazia = "sem horário configurado" (mesmo significado de
// antes: IA nunca avisa horário ao transferir, e o aviso automático de fora
// de horário, se ativado pra essa conexão, fica sem efeito).
const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexDirection: "column",
		gap: 2,
	},
	dayRow: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "3px 0",
	},
	dayLabel: {
		width: 92,
		flexShrink: 0,
		fontSize: 13,
		fontWeight: 500,
		color: "#334155",
	},
	hourSelect: {
		fontSize: 13,
		minWidth: 64,
	},
	toLabel: {
		fontSize: 13,
		color: "#94a3b8",
	},
	previewBox: {
		marginTop: 10,
		padding: "8px 12px",
		borderRadius: 6,
		backgroundColor: "#f1f5f9",
		fontSize: 12.5,
		color: "#475569",
	},
}));

const DAY_NAMES = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
// ordem de exibição/negócio Seg→Dom, mesma convenção já usada no resto do
// projeto (item 67 do HISTORICO_AJUSTES.md, "Movimento por Dia da Semana")
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const START_HOURS = Array.from({ length: 24 }, (_, i) => i); // 0..23
const END_HOURS = Array.from({ length: 24 }, (_, i) => i + 1); // 1..24

const emptyDay = () => ({ enabled: false, start: 8, end: 18 });

const parseValue = raw => {
	const days = {};
	for (let d = 0; d < 7; d++) days[d] = emptyDay();

	if (raw) {
		try {
			const parsed = JSON.parse(raw);
			(parsed.windows || []).forEach(w => {
				(w.days || []).forEach(d => {
					if (days[d]) days[d] = { enabled: true, start: w.start, end: w.end };
				});
			});
		} catch {
			// JSON antigo/malformado (editado na mão antes desse componente
			// existir) — abre tudo desmarcado, usuário refaz do zero pela UI
		}
	}
	return days;
};

// Agrupa dias consecutivos com o mesmo horário num rótulo legível, ex:
// "Segunda a Sexta, das 8h às 16h, e Sábado, das 8h às 12h" — mesmo estilo já
// usado nas conexões configuradas na mão antes desse editor existir.
const generateLabel = days => {
	const groups = [];
	let current = null;

	DAY_ORDER.forEach(dayIndex => {
		const day = days[dayIndex];
		if (!day.enabled) {
			current = null;
			return;
		}
		if (current && current.start === day.start && current.end === day.end) {
			current.dayIndexes.push(dayIndex);
		} else {
			current = { start: day.start, end: day.end, dayIndexes: [dayIndex] };
			groups.push(current);
		}
	});

	if (groups.length === 0) return "";

	const describeGroup = g => {
		const names = g.dayIndexes.map(d => DAY_NAMES[d]);
		const isRange = names.length >= 3;
		const daysLabel = isRange
			? `${names[0]} a ${names[names.length - 1]}`
			: names.join(" e ");
		return `${daysLabel}, das ${g.start}h às ${g.end}h`;
	};

	return groups.map(describeGroup).join(", e ");
};

const buildJson = days => {
	const windows = [];
	DAY_ORDER.forEach(dayIndex => {
		const day = days[dayIndex];
		if (!day.enabled) return;
		const existing = windows.find(w => w.start === day.start && w.end === day.end);
		if (existing) {
			existing.days.push(dayIndex);
		} else {
			windows.push({ days: [dayIndex], start: day.start, end: day.end });
		}
	});

	if (windows.length === 0) return "";

	return JSON.stringify({ label: generateLabel(days), windows });
};

const BusinessHoursEditor = ({ value, onChange }) => {
	const classes = useStyles();
	const [days, setDays] = useState(() => parseValue(value));

	const updateDay = (dayIndex, patch) => {
		const next = { ...days, [dayIndex]: { ...days[dayIndex], ...patch } };
		setDays(next);
		onChange(buildJson(next));
	};

	const label = generateLabel(days);
	const anyEnabled = Object.values(days).some(d => d.enabled);

	return (
		<div className={classes.root}>
			{DAY_ORDER.map(dayIndex => {
				const day = days[dayIndex];
				return (
					<div className={classes.dayRow} key={dayIndex}>
						<Switch
							size="small"
							color="primary"
							checked={day.enabled}
							onChange={e => updateDay(dayIndex, { enabled: e.target.checked })}
						/>
						<Typography className={classes.dayLabel}>
							{DAY_NAMES[dayIndex]}
						</Typography>
						{day.enabled ? (
							<>
								<Select
									className={classes.hourSelect}
									value={day.start}
									onChange={e => updateDay(dayIndex, { start: Number(e.target.value) })}
								>
									{START_HOURS.map(h => (
										<MenuItem key={h} value={h}>{h}h</MenuItem>
									))}
								</Select>
								<Typography className={classes.toLabel}>às</Typography>
								<Select
									className={classes.hourSelect}
									value={day.end}
									onChange={e => updateDay(dayIndex, { end: Number(e.target.value) })}
								>
									{END_HOURS.map(h => (
										<MenuItem key={h} value={h}>{h}h</MenuItem>
									))}
								</Select>
							</>
						) : (
							<Typography className={classes.toLabel}>fechado</Typography>
						)}
					</div>
				);
			})}

			{anyEnabled ? (
				<TextField
					label="Descrição deste horário (gerada automaticamente)"
					value={label}
					disabled
					fullWidth
					variant="outlined"
					margin="dense"
					helperText="Não é enviada sozinha — entra dentro dos avisos automáticos (ex: 'No momento estamos fora do horário de atendimento (⟵ isto aqui). Retornamos amanhã às 8h...') e serve de referência pra IA responder perguntas sobre horário."
					style={{ marginTop: 8 }}
				/>
			) : (
				<div className={classes.previewBox}>
					Nenhum dia marcado — a IA nunca avisa horário ao transferir pra
					humano, e o aviso automático de fora de horário (se ativado)
					fica sem efeito pra essa conexão.
				</div>
			)}
		</div>
	);
};

export default BusinessHoursEditor;
